import { getAppState } from "../state";
import { routes } from "../routes";
import { historyMethods } from "../utils/historyMethods";
import { trackEvent } from "./analyticsService";

/**
 * Checks if the previous visited page was a detail page of a different section. For example, this function would
 * return true if the user came to a series detail page from a movies detail page. This can only happen by using the
 * deep linking feature. In case the function can't ensure the previous page was a detail page, it will return false.
 * @return {boolean} true if the previous page was a detail page from a section different from the current detail
 * page's section.
 * @public
 */
export function isComingFromDetailPageScope() {
  const {
    location: { pathname, referrer },
    match: {
      params: { titleId }
    }
  } = this.props;
  const sameDetailExp = new RegExp("/" + titleId);
  return sameDetailExp.test(pathname) && sameDetailExp.test(referrer);
}

/**
 * Redirects to a new path name, passing some location data through history location state if present,
 * and hides global modal in case it's present or active. Needs a React component class context since
 * this method is making use of this.props.
 * @param {String} path - New path name.
 * @param {Object} locationData - Data object to be passed through location state.
 * @param {String} navigationMode - History navigation mode method name.
 * @public
 */
export async function redirectTo(pathname, locationData, navigationMode = historyMethods.PUSH) {
  const {
    stateActions: { hideGlobalModal },
    history
  } = this.props;
  if (getAppState().signupPromptActive) {
    await setSignupPromptStatus(this?.promptRef);
  }
  !!getAppState().globalModal && hideGlobalModal();
  history[navigationMode]({
    pathname,
    ...(locationData && {
      state: locationData
    })
  });
}

const setSignupPromptStatus = async promptRef => {
  if (promptRef && promptRef.current) {
    getAppState().signupPromptActive = false;
  }
};

/**
 * Gets navigation references for next/back navigation actions when reaching login page from detail
 * page replacing pathname. Needs a React component class context since this method is making use of this.props.
 * @public
 */
export function getNavigationReferences() {
  return {
    nextPageReference: { pathname: this.props.location.pathname },
    backPageReference: { pathname: this.props.location.pathname }
  };
}

/**
 * Tracks an event related to detail page navigation (when pressing either on trailer button or on episodes
 * one) and makes the redirection to a provided page. Needs a React component class context since this method is making use of this.props.
 * @param {String} page - Page to be tracked.
 * @param {String} action - Event action to be tracked.
 * @param {String} newPathname - Pathname where to be redirected.
 * @param {Object} locationData - Data object to be passed through location state.
 * @public
 */
export function trackAndRedirectHandler(page, action, newPathname, locationData, label = null, data = null) {
  trackEvent(action, page, label, data);
  redirectTo.call(this, newPathname, locationData);
}

export function getLoginRedirectionData() {
  const { history } = this.props;
  const hasHistoryState = !!(history.location && history.location.state);
  const nextPage = hasHistoryState && history.location.state.nextPage;
  const nextPagePathname = (nextPage && history.location.state.nextPage.pathname) || nextPage;
  const navigationMode = (nextPage && history.location.state.nextPage.navigationMode) || historyMethods.REPLACE;
  let referrerPage =
    history.location &&
    history.location.referrer !== history.location.pathname &&
    history.location.referrer !== routes.pin &&
    history.location.referrer !== routes.pinSignup &&
    history.location.referrer !== routes.welcome &&
    history.location.referrer;

  const redirectPath = nextPagePathname || referrerPage || routes.home;
  return { navigationMode, redirectPath };
}
